import React, { useState } from "react";
import { ShieldCheckIcon as ShieldCheck, QuestionIcon as Question, ShieldWarningIcon as ShieldWarning } from "@phosphor-icons/react";

/**
 * Public-facing microchip-status pill.
 *
 * Two variants, driven by `protected`:
 *
 *   protected=true  → green "Microchip-protected · ·{last4}" pill.
 *                     Tooltip: explains the registry's verification flow
 *                     (forces the buyer to read the chip back to confirm
 *                     the dog before ownership moves; combined with
 *                     escrow this blocks bait-and-switch at handoff).
 *
 *   protected=false → gray "Not microchip-verified" pill.
 *                     Tooltip: honest disclosure that ownership
 *                     transfers on this dog will rely on document
 *                     review only, plus a coachy "consider asking the
 *                     seller to chip before the meet" line.
 *
 * Both variants surface a Question icon so the user knows the pill is
 * inspectable on hover/focus.
 */
export default function MicrochipProtectedBadge({
  protected: isProtected = true,
  last4,
  size = "md",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const sizes = {
    sm: { pad: "px-2 py-0.5 text-[10px]", icon: 11 },
    md: { pad: "px-2.5 py-1 text-[11px]", icon: 12 },
    lg: { pad: "px-3 py-1.5 text-[12px]", icon: 13 },
  };
  const s = sizes[size] || sizes.md;

  // Embossed-medallion treatment: subtle vertical gradient + an inset
  // highlight ring on top + soft drop shadow. Keeps the green/gray
  // semantic but gives the pill physical depth so it doesn't feel
  // pasted on. Inline style keeps the gradient + shadow tokens self-
  // contained so a Tailwind purge can't strip them.
  const isOk = isProtected;
  const protectedStyle = {
    background: "linear-gradient(180deg, #F1FBEF 0%, #DFF4DC 100%)",
    color: "#1F5C1F",
    border: "1px solid rgba(102,162,102,0.55)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(31,92,31,0.18), 0 1px 2px rgba(31,92,31,0.15)",
  };
  const unverifiedStyle = {
    background: "linear-gradient(180deg, #FAFAFA 0%, #ECECEC 100%)",
    color: "#4A4A4A",
    border: "1px solid rgba(122,122,122,0.35)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
  };
  const pillStyle = isOk ? protectedStyle : unverifiedStyle;
  const Icon = isProtected ? ShieldCheck : ShieldWarning;
  const label = isProtected ? "Microchip-protected" : "Not microchip-verified";
  const testId = isProtected
    ? "microchip-protected-badge"
    : "microchip-unverified-badge";

  return (
    <span className={`relative inline-flex ${className}`} data-testid={testId}>
      <span
        className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-[0.14em] ${s.pad}`}
        style={pillStyle}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        tabIndex={0}
        role="button"
        aria-label={`${label} — what does this mean?`}
      >
        <Icon size={s.icon} weight="fill" />
        {label}
        {isProtected && last4 && (
          <span className="font-mono font-normal opacity-75 ml-0.5">
            ·{last4}
          </span>
        )}
        <Question size={s.icon - 1} weight="bold" className="opacity-60" />
      </span>
      {open && (
        <span
          role="tooltip"
          className="absolute left-0 top-[calc(100%+6px)] z-30 w-[300px] rounded-lg bg-[#0E0E0E] text-white p-3 shadow-xl text-[12px] leading-relaxed font-normal normal-case tracking-normal"
        >
          {isProtected ? (
            <>
              <strong className="text-gold block text-[10px] uppercase tracking-[0.2em] font-bold mb-1">
                Why this matters
              </strong>
              This dog is registered to a unique microchip on file. Our
              ownership-transfer flow requires the buyer to physically
              read the chip and verify it back to the registry before
              ownership can move — three wrong attempts triggers a
              cool-down. Combined with Stripe escrow, neither the
              seller can swap the dog at handoff nor the buyer can
              renege after taking possession.
            </>
          ) : (
            <>
              <strong className="text-[#FFD37D] block text-[10px] uppercase tracking-[0.2em] font-bold mb-1">
                What this means
              </strong>
              This dog is <strong>not</strong> registered to a microchip.
              Ownership transfers will rely on document review only —
              there's no automatic chip-match check at handoff. Many
              honest breeders skip chipping for young puppies (chips
              are typically implanted at the first vet visit), but it
              does mean less fraud protection on the meet. Consider
              asking the seller to chip the dog before the meet, or to
              do the chipping at a vet visit you both attend.
            </>
          )}
        </span>
      )}
    </span>
  );
}
